import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from './Modal';
import { Button, Checkbox } from '../atoms';
import { FormField } from '../molecues/FormField';
import type {
  PaymentMethod,
  PaymentMethodCreateRequest,
  PaymentMethodType,
  PaymentMethodUpdateRequest,
} from '../../types/api/payment-method';
import { PAYMENT_METHOD_TYPES } from '../../types/api/payment-method';
import { resolveAssetUrl } from '../../utils/url';

export interface PaymentMethodFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: PaymentMethodCreateRequest | PaymentMethodUpdateRequest,
    files?: { logoFile?: File | null; qrImageFile?: File | null }
  ) => void | Promise<void>;
  mode: 'create' | 'edit';
  paymentMethod?: PaymentMethod;
}

type PaymentMethodFormState = {
  name: string;
  paymentType: PaymentMethodType;
  description: string;
  logoUrl: string;
  qrImageUrl: string;
  isActive: boolean;
  sortOrder: number;
};

const createDefaultState = (): PaymentMethodFormState => ({
  name: '',
  paymentType: 'OTHER',
  description: '',
  logoUrl: '',
  qrImageUrl: '',
  isActive: true,
  sortOrder: 0,
});

export const PaymentMethodFormModal: React.FC<PaymentMethodFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  paymentMethod,
}) => {
  const [formData, setFormData] = useState<PaymentMethodFormState>(createDefaultState());
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [qrImageFile, setQrImageFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [qrPreview, setQrPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (paymentMethod && mode === 'edit' && isOpen) {
      setFormData({
        name: paymentMethod.name || '',
        paymentType: paymentMethod.paymentType || 'OTHER',
        description: paymentMethod.description || '',
        logoUrl: paymentMethod.logoUrl || '',
        qrImageUrl: paymentMethod.qrImageUrl || '',
        isActive: paymentMethod.isActive ?? true,
        sortOrder: paymentMethod.sortOrder ?? 0,
      });
      setLogoPreview(
        paymentMethod.logoUrl ? (resolveAssetUrl(paymentMethod.logoUrl) ?? null) : null
      );
      setQrPreview(
        paymentMethod.qrImageUrl ? (resolveAssetUrl(paymentMethod.qrImageUrl) ?? null) : null
      );
      setLogoFile(null);
      setQrImageFile(null);
    } else if (isOpen) {
      setFormData(createDefaultState());
      setLogoPreview(null);
      setQrPreview(null);
      setLogoFile(null);
      setQrImageFile(null);
    }
    setErrors({});
  }, [paymentMethod, mode, isOpen]);

  const handleChange = (field: keyof PaymentMethodFormState, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validateImageFile = (file: File, fieldKey: 'logo' | 'qrImage'): string | null => {
    if (!file.type.startsWith('image/')) {
      return 'Please select a valid image file';
    }
    const maxSizeMb = 5;
    if (file.size > maxSizeMb * 1024 * 1024) {
      return `Image size must be less than ${maxSizeMb}MB`;
    }
    if (fieldKey === 'qrImage') {
      // QR codes are usually small, but we keep same limit; no extra rule here.
    }
    return null;
  };

  const setPreviewFromFile = (file: File, setPreview: (url: string) => void) => {
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateImageFile(file, 'logo');
    if (err) {
      setErrors((prev) => ({ ...prev, logo: err }));
      return;
    }
    setLogoFile(file);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.logo;
      return next;
    });
    setPreviewFromFile(file, (url) => setLogoPreview(url));
  };

  const handleQrChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateImageFile(file, 'qrImage');
    if (err) {
      setErrors((prev) => ({ ...prev, qrImage: err }));
      return;
    }
    setQrImageFile(file);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.qrImage;
      return next;
    });
    setPreviewFromFile(file, (url) => setQrPreview(url));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length > 200) {
      newErrors.name = 'Name must be less than 200 characters';
    }

    if (!formData.paymentType) {
      newErrors.paymentType = 'Payment type is required';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must be less than 1000 characters';
    }

    if (formData.sortOrder < 0) {
      newErrors.sortOrder = 'Sort order must be 0 or greater';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const hasAnyFile = useMemo(() => Boolean(logoFile || qrImageFile), [logoFile, qrImageFile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      if (mode === 'create') {
        const payload: PaymentMethodCreateRequest = {
          name: formData.name.trim(),
          paymentType: formData.paymentType,
          description: formData.description.trim() || null,
          logoUrl: formData.logoUrl.trim() || null,
          qrImageUrl: formData.qrImageUrl.trim() || null,
          isActive: formData.isActive,
          sortOrder: formData.sortOrder,
        };
        await onSubmit(payload, { logoFile, qrImageFile });
      } else {
        const payload: PaymentMethodUpdateRequest = {
          name: formData.name.trim() || undefined,
          paymentType: formData.paymentType || undefined,
          description: formData.description.trim() ? formData.description.trim() : null,
          logoUrl: formData.logoUrl.trim() ? formData.logoUrl.trim() : null,
          qrImageUrl: formData.qrImageUrl.trim() ? formData.qrImageUrl.trim() : null,
          isActive: formData.isActive,
          sortOrder: formData.sortOrder,
        };
        await onSubmit(payload, { logoFile, qrImageFile });
      }
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setFormData(createDefaultState());
    setErrors({});
    setLogoFile(null);
    setQrImageFile(null);
    setLogoPreview(null);
    setQrPreview(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === 'create' ? 'Create Payment Method' : 'Edit Payment Method'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-4">
          <p className="text-2xl font-semibold text-gray-700 text-left">Basic Information</p>

          <FormField
            label="Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={errors.name}
            required
            placeholder="e.g., KBZPay"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
              Payment Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.paymentType}
              onChange={(e) => handleChange('paymentType', e.target.value as PaymentMethodType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
            >
              {PAYMENT_METHOD_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            {errors.paymentType && <p className="text-red-500 text-xs mt-1">{errors.paymentType}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
              placeholder="Optional"
            />
            {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
          </div>

          <FormField
            label="Sort Order"
            type="number"
            value={formData.sortOrder}
            onChange={(e) => handleChange('sortOrder', parseInt(e.target.value) || 0)}
            error={errors.sortOrder}
            min={0}
          />

          <div className="flex items-center gap-4">
            <div className="flex items-center">
              <Checkbox
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
              />
              <label className="ml-2 text-sm text-gray-700">Active</label>
            </div>
            {hasAnyFile && (
              <p className="text-xs text-gray-500">
                Upload selected: files will override URL fields on the backend.
              </p>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-2xl font-semibold text-gray-700 text-left">Logo</p>

          <FormField
            label="Logo URL (optional)"
            value={formData.logoUrl}
            onChange={(e) => handleChange('logoUrl', e.target.value)}
            error={errors.logoUrl}
            placeholder="https://..."
          />

          <div className="border border-gray-300 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-1.5 text-left">
              Logo File (optional)
            </label>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded bg-gray-100 flex items-center justify-center overflow-hidden border">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                ) : (
                  <span className="text-xs text-gray-400">No logo</span>
                )}
              </div>
              <div>
                <input type="file" accept="image/*" onChange={handleLogoChange} className="block text-sm text-gray-700" />
                <p className="mt-1 text-xs text-gray-500">JPEG, PNG or WebP. Max size 5MB.</p>
                {errors.logo && <p className="text-sm text-red-600 mt-1.5">{errors.logo}</p>}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-2xl font-semibold text-gray-700 text-left">QR Image</p>

          <FormField
            label="QR Image URL (optional)"
            value={formData.qrImageUrl}
            onChange={(e) => handleChange('qrImageUrl', e.target.value)}
            error={errors.qrImageUrl}
            placeholder="https://..."
          />

          <div className="border border-gray-300 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-1.5 text-left">
              QR Image File (optional)
            </label>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded bg-gray-100 flex items-center justify-center overflow-hidden border">
                {qrPreview ? (
                  <img src={qrPreview} alt="QR preview" className="w-full h-full object-contain" />
                ) : (
                  <span className="text-xs text-gray-400">No QR</span>
                )}
              </div>
              <div>
                <input type="file" accept="image/*" onChange={handleQrChange} className="block text-sm text-gray-700" />
                <p className="mt-1 text-xs text-gray-500">JPEG, PNG or WebP. Max size 5MB.</p>
                {errors.qrImage && <p className="text-sm text-red-600 mt-1.5">{errors.qrImage}</p>}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" onClick={handleClose} disabled={isSubmitting} variant="secondary">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default PaymentMethodFormModal;

